/**
 * Revenue Calculation — pure render rules for the assumptions panel (card 3065).
 *
 * These live in `utils/*.js`, NOT in the `.jsx` component, for a measured reason:
 * `jest.config.js` declares `moduleFileExtensions: ['js', 'mjs', 'json']` and
 * `transform: {}`, so a `.jsx` file cannot be imported by the suite at all. A render
 * rule that only exists inside a component is a render rule no test can pin — which is
 * exactly how "$0.00" quietly replaces "unknown" on one surface and not another. The
 * precedent is `web/utils/fovReconciliation.js`, tested by card 2844.
 *
 * 🔑 THE ONE RULE THIS FILE ENFORCES: a RULED ZERO and an UNKNOWN are different facts and
 * must never render the same. A ruled zero is a decision X made and reads
 * "$0.00 (ruled zero — assumption)"; an unknown is a gap and reads `— / Missing`, the
 * literal owned by `revenueMetrics.js`. Neither string is authored twice.
 */

import { formatPrice } from './formatters';
import { MISSING_TEXT } from './revenueMetrics';

/** The suffix that marks a zero as a DECISION rather than a gap. Pinned once. */
export const RULED_ZERO_SUFFIX = '(ruled zero — assumption)';

/** Re-exported so a consumer of this module never has to reach for a second "unknown" string. */
export { MISSING_TEXT };

/**
 * PURE. True only when a line is a genuine finite integer-cents amount.
 *
 * ⛔ DO NOT rewrite as `Number.isFinite(Number(line?.amountCents))`. `Number(null) === 0`,
 * which is finite, so an unresolved line would render "$0.00" — the precise bug
 * `revenueMetrics.isBlockedMetric` documents having shipped once already. The `typeof`
 * test rejects null/undefined/'' BY TYPE, before any coercion can invent a zero.
 */
export const hasRuledAmount = (line) =>
  typeof line?.amountCents === 'number' && Number.isFinite(line.amountCents);

/**
 * PURE. What a ruled line renders as.
 *
 * Three outcomes, deliberately distinguishable by a reader AND by a test:
 *   • no amount at all  → `— / Missing`      (a gap; never a dollar figure)
 *   • a ruled zero      → `$0.00 (ruled zero — assumption)`
 *   • any other amount  → `$50.00`
 */
export const formatAssumptionAmount = (line) => {
  if (!hasRuledAmount(line)) return MISSING_TEXT;
  const money = formatPrice(line.amountCents);
  return line.isRuledZero ? `${money} ${RULED_ZERO_SUFFIX}` : money;
};

/**
 * PURE. The provenance sentence for one line.
 *
 * Falls back to the label the API computed, and then to a self-describing string — a panel
 * must never render an empty provenance, because an empty one reads as "no claim made"
 * when the truth is "we failed to send it".
 */
export const describeAssumptionLabel = (line) =>
  line?.assumptionLabel
  || (line?.effectiveFrom && line?.authorizedBy
    ? `ASSUMPTION — effective ${line.effectiveFrom}, authorized by ${line.authorizedBy}`
    : 'ASSUMPTION — provenance not supplied by the API');

/**
 * PURE. Words that would misrepresent a dated assumption as a sourced figure.
 * Mirrors `costAssumptions.FORBIDDEN_PROVENANCE_TERMS`; duplicated here on purpose so the
 * VIEW can refuse to render drifted content even if the API stops guarding it.
 */
export const FORBIDDEN_RENDER_TERMS = Object.freeze(['invoice', 'verified', 'vendor-quoted']);

/**
 * PURE. Any forbidden term present in what this panel is about to render.
 *
 * Returns the offending terms, not a boolean, so a failing check names them. The haystack is
 * built from the payload itself rather than from the DOM, so the check runs before render.
 */
export const findForbiddenRenderTerms = (payload) => {
  const haystack = JSON.stringify(payload ?? {}).toLowerCase();
  return FORBIDDEN_RENDER_TERMS.filter((term) => haystack.includes(term));
};

/**
 * PURE. Is this payload safe and complete enough to render as authoritative?
 *
 * ⛔ A panel that renders a broken payload as if it were fine is worse than one that renders
 * nothing: the reader cannot tell. `reasons` is an array so the panel can SAY what is wrong.
 */
export const gradeAssumptionsPayload = (payload) => {
  const lines = payload?.assumptions ?? [];
  const reasons = [
    ...(lines.length > 0 ? [] : ['The assumptions endpoint returned no lines.']),
    ...(payload?.provenanceGuard?.ok === false
      ? [`Registry provenance guard failed on: ${(payload.provenanceGuard.offenders ?? []).join(', ')}.`]
      : []),
    ...((payload?.registryDefects ?? []).length > 0
      ? [`${payload.registryDefects.length} registry entr${payload.registryDefects.length === 1 ? 'y is' : 'ies are'} malformed.`]
      : []),
    ...((payload?.unjoinedAnswerKeys ?? []).length > 0
      ? [`${payload.unjoinedAnswerKeys.length} answered question(s) point at a registry key that no longer exists.`]
      : []),
    ...findForbiddenRenderTerms(payload).map(
      (term) => `Payload contains the forbidden provenance term "${term}" — these are dated assumptions, not sourced figures.`
    )
  ];
  return { ok: reasons.length === 0, reasons, lineCount: lines.length };
};

/**
 * PURE. Split the lines into ruled zeros and priced lines.
 *
 * The panel renders both groups under their own heading so a screenshot can show, on one
 * page, that a decided zero and an unknown are not the same thing.
 */
export const partitionAssumptions = (lines = []) => ({
  ruledZero: lines.filter((line) => line.isRuledZero),
  priced: lines.filter((line) => !line.isRuledZero)
});
